import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/common/UI";
import { fmtDateTime } from "@/lib/format";
import { History, ChevronDown, ChevronUp } from "lucide-react";

const ACTION_TONE = {
    CREATE: "text-slate-600",
    UPDATE: "text-slate-600",
    SUBMIT: "text-blue-600",
    APPROVE: "text-emerald-600",
    REJECT: "text-red-600",
    DEACTIVATE: "text-red-600",
    ACTIVATE: "text-emerald-600",
    DELETE: "text-red-600",
    RESET_PASSWORD: "text-amber-600",
    RATE_CHANGE: "text-[hsl(var(--copper))]",
    RECALCULATE: "text-[hsl(var(--copper))]",
};

export default function AuditTrail({ entityType, entityId, defaultOpen = false, title = "Audit Log" }) {
    const [rows, setRows] = useState(null);
    const [open, setOpen] = useState(defaultOpen);

    useEffect(() => {
        if (!open || rows !== null) return;
        api.get(`/audit-log?entity_type=${entityType}&entity_id=${entityId}&limit=50`)
            .then((r) => setRows(r.data))
            .catch(() => setRows([]));
    }, [open, rows, entityType, entityId]);

    return (
        <Card data-testid={`audit-trail-${entityType}`}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full px-4 py-3 border-b border-slate-200 flex items-center justify-between hover:bg-slate-50/50"
                data-testid={`audit-trail-toggle-${entityType}`}
            >
                <div className="flex items-center gap-2">
                    <History className="h-3.5 w-3.5 text-slate-400" />
                    <div className="font-display text-[14px] font-semibold text-slate-900">{title}</div>
                </div>
                {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
            </button>
            {open && (
                <div className="p-2">
                    {rows === null ? (
                        <div className="px-3 py-4 text-[12px] text-slate-400">Loading…</div>
                    ) : rows.length === 0 ? (
                        <div className="px-3 py-4 text-[12px] text-slate-400 text-center">No audit entries for this record yet.</div>
                    ) : (
                        <table className="w-full text-[12px]">
                            <thead>
                                <tr className="border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
                                    <th className="text-left py-2 px-3">Date</th>
                                    <th className="text-left py-2 px-3">Action</th>
                                    <th className="text-left py-2 px-3">By</th>
                                    <th className="text-left py-2 px-3">Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} className="border-b border-slate-50 last:border-b-0" data-testid={`audit-row-${r.id}`}>
                                        <td className="py-2 px-3 font-mono text-[11px] text-slate-500 whitespace-nowrap">{fmtDateTime(r.timestamp)}</td>
                                        <td className="py-2 px-3"><span className={`font-mono font-semibold text-[11px] ${ACTION_TONE[r.action] || "text-slate-600"}`}>{r.action}</span></td>
                                        <td className="py-2 px-3 text-slate-700">
                                            <div>{r.user_name || "—"}</div>
                                            <div className="text-[10px] text-slate-400 font-mono">{r.user_role}</div>
                                        </td>
                                        <td className="py-2 px-3 text-slate-600">{r.summary}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </Card>
    );
}
